import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../config/api';
import SchoolLayout from '../components/SchoolLayout';
import SchoolDefaultTimesField from '../components/SchoolDefaultTimesField';
import SchoolWeeklyDismissalDefaultsField from '../components/SchoolWeeklyDismissalDefaultsField';
import SchoolGradeAliasesField from '../components/SchoolGradeAliasesField';
import SchoolLogoField from '../components/SchoolLogoField';
import SchoolOnboardingEmailField from '../components/SchoolOnboardingEmailField';
import SchoolOnboardingWhatsAppField from '../components/SchoolOnboardingWhatsAppField';
import { isSchoolAdminUser, isSuperAdminUser } from '../lib/adminAccess';
import { sendSupportRequest } from '../lib/support';
import { getSchoolLocalParts } from '../lib/schoolTime';

const APP_VERSION = 'v0.1.0';
const STUDENT_GROUPING_OPTIONS = [
  {
    value: 'grades',
    label: 'Grades',
    description: 'Teachers select grade-level groups only.',
  },
  {
    value: 'grades_and_splits',
    label: 'Grades and Splits',
    description: 'Teachers can select each grade or a split inside that grade.',
  },
  {
    value: 'splits_only',
    label: 'Splits Only',
    description: 'Teachers select split names across grades; unassigned students stay covered by grade fallback.',
  },
];
const STUDENT_GROUPING_MODE_VALUES = new Set(STUDENT_GROUPING_OPTIONS.map((option) => option.value));

function isSchoolWideWeekdayDefault(row) {
  return row && (row.grade === null || row.grade === undefined) && (row.gender === null || row.gender === undefined);
}

function normalizeStudentGroupingMode(value) {
  return STUDENT_GROUPING_MODE_VALUES.has(value) ? value : 'grades';
}

function formatTime(value) {
  if (!value) return 'Not configured';
  const [hours = '0', minutes = '0'] = String(value).split(':');
  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDate(value) {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not scheduled';
  return date.toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' });
}

function formatLabel(value) {
  return String(value || 'Not available').replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}

function loadNotificationSettings(schoolId) {
  if (typeof window === 'undefined') {
    return {
      dismissalStarted: true,
      systemAlerts: true,
      calendarUpdates: true,
    };
  }

  try {
    const stored = window.localStorage.getItem(`super-admin-settings-${schoolId}`);
    if (!stored) {
      return {
        dismissalStarted: true,
        systemAlerts: true,
        calendarUpdates: true,
      };
    }
    return JSON.parse(stored);
  } catch {
    return {
      dismissalStarted: true,
      systemAlerts: true,
      calendarUpdates: true,
    };
  }
}

export default function SystemPreferencesPage({ token, navigate, schoolId, user, bare = false }) {
  const [school, setSchool] = useState(null);
  const [defaults, setDefaults] = useState([]);
  const [notifications, setNotifications] = useState(() => loadNotificationSettings(schoolId));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [billingMessage, setBillingMessage] = useState('');
  const [billingSending, setBillingSending] = useState(false);
  const [billingError, setBillingError] = useState(null);
  const [billingSent, setBillingSent] = useState(false);
  const [messagingEnabled, setMessagingEnabled] = useState(true);
  const [messagingSaving, setMessagingSaving] = useState(false);
  const [messagingError, setMessagingError] = useState('');
  const [studentGroupingMode, setStudentGroupingMode] = useState('grades');
  const [studentGroupingSaving, setStudentGroupingSaving] = useState(false);
  const [studentGroupingError, setStudentGroupingError] = useState('');
  const [gradeAliasesOpen, setGradeAliasesOpen] = useState(false);
  const [onboardingEmailOpen, setOnboardingEmailOpen] = useState(false);
  const [onboardingWhatsAppOpen, setOnboardingWhatsAppOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    apiFetch(`/schools/${schoolId}`, {}, token)
      .then(async (schoolRes) => {
        if (cancelled) return;

        const nextSchool = schoolRes.data;
        const local = getSchoolLocalParts(nextSchool);
        const localDate = `${local.year}-${String(local.month).padStart(2, '0')}-${String(local.day).padStart(2, '0')}`;
        const defaultsRes = await apiFetch(
          `/dismissal-time-defaults/active?school_id=${schoolId}&date=${localDate}`,
          {},
          token
        ).catch(() => ({ data: [] }));

        if (cancelled) return;
        setSchool(nextSchool);
        setDefaults(defaultsRes.data || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [schoolId, token]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(`super-admin-settings-${schoolId}`, JSON.stringify(notifications));
  }, [notifications, schoolId]);

  useEffect(() => {
    if (school) setMessagingEnabled(Number(school.messaging_enabled) !== 0);
  }, [school]);

  useEffect(() => {
    if (school) setStudentGroupingMode(normalizeStudentGroupingMode(school.student_grouping_mode));
  }, [school]);

  const schoolWideWeekdayCount = useMemo(
    () => defaults.filter(isSchoolWideWeekdayDefault).length,
    [defaults]
  );

  const paymentSupportEmail = school?.it_email || school?.main_email || '';
  const canEditSchoolProfile = isSuperAdminUser(user);
  const canManageLogo = isSuperAdminUser(user) || isSchoolAdminUser(user);
  const canManageDefaults = canManageLogo;
  const canManageGradeAliases = canManageLogo;
  const canManageOnboardingEmail = canManageLogo;
  const canManageOnboardingWhatsApp = canManageLogo;
  const canManageMessaging = isSuperAdminUser(user) || isSchoolAdminUser(user);
  const canManageStudentGrouping = canManageMessaging;

  const toggle = (key) => {
    setNotifications((current) => ({ ...current, [key]: !current[key] }));
  };

  const handleMessagingToggle = async () => {
    if (messagingSaving || !canManageMessaging) return;

    const next = !messagingEnabled;
    setMessagingSaving(true);
    setMessagingError('');
    setMessagingEnabled(next);

    try {
      const res = await apiFetch(
        `/schools/${schoolId}`,
        { method: 'PUT', body: JSON.stringify({ messaging_enabled: next ? 1 : 0 }) },
        token
      );
      const savedValue = res?.data?.messaging_enabled ?? (next ? 1 : 0);
      setMessagingEnabled(Number(savedValue) !== 0);
      setSchool((current) => (current ? { ...current, messaging_enabled: savedValue } : current));
    } catch (err) {
      setMessagingEnabled(!next);
      setMessagingError(err.message || "We couldn't update the messaging setting. Please try again.");
    } finally {
      setMessagingSaving(false);
    }
  };

  const handleStudentGroupingChange = async (nextMode) => {
    const normalizedMode = normalizeStudentGroupingMode(nextMode);
    if (studentGroupingSaving || !canManageStudentGrouping || normalizedMode === studentGroupingMode) return;

    const previousMode = studentGroupingMode;
    setStudentGroupingSaving(true);
    setStudentGroupingError('');
    setStudentGroupingMode(normalizedMode);

    try {
      const res = await apiFetch(
        `/schools/${schoolId}`,
        { method: 'PUT', body: JSON.stringify({ student_grouping_mode: normalizedMode }) },
        token
      );
      const savedMode = normalizeStudentGroupingMode(res?.data?.student_grouping_mode);
      setStudentGroupingMode(savedMode);
      setSchool((current) => (current ? { ...current, student_grouping_mode: savedMode } : current));
    } catch (err) {
      setStudentGroupingMode(previousMode);
      setStudentGroupingError(err.message || "We couldn't update the student grouping setting. Please try again.");
    } finally {
      setStudentGroupingSaving(false);
    }
  };

  const handleBillingModalOpen = () => {
    setBillingModalOpen(true);
    setBillingError(null);
    setBillingSent(false);
  };

  const handleBillingModalClose = () => {
    setBillingModalOpen(false);
    setBillingError(null);
    setBillingSent(false);
  };

  const handleBillingSend = async () => {
    const message = billingMessage.trim();
    if (!message || billingSending) return;

    setBillingError(null);
    setBillingSending(true);

    try {
      await sendSupportRequest({
        supportType: 'billing',
        message,
        user,
        school,
        schoolId,
      });
      setBillingMessage('');
      setBillingSent(true);
    } catch (err) {
      console.error('Failed to send billing support message:', err);
      setBillingError("We couldn't send your message. Please try again.");
    } finally {
      setBillingSending(false);
    }
  };

  const content = (
    <div className="sap-page">
        {error ? <div className="sa-error sap-inline-error">{error}</div> : null}

        {loading ? (
          <div className="sa-empty">Loading...</div>
        ) : !school ? (
          <div className="sa-error">School not found</div>
        ) : (
          <div className="ssp-shell">
            <div className="ssp-top-grid">
              <div className="ssp-column ssp-profile-column">
                <section className="sap-parents-panel">
                  <div className="sap-panel-heading">
                    <div>
                      <h2>School Profile</h2>
                      <p>Core identity and configuration</p>
                    </div>
                    {canEditSchoolProfile ? (
                      <button className="sa-btn sa-btn-secondary" onClick={() => navigate('school-edit', school)}>
                        Edit
                      </button>
                    ) : null}
                  </div>

                  <div className="sap-section-card ssp-card">
                    <InfoRow label="School Name" value={school.legal_name} />
                    <InfoRow label="Address" value={[school.street_address, school.city, school.state, school.zip_code].filter(Boolean).join(', ')} />
                    <InfoRow label="Time Zone" value={school.time_zone} />
                    <InfoRow label="General Dismissal Time" value={formatTime(school.default_dismissal_time)} />
                    <InfoRow label="Weekday Defaults" value={schoolWideWeekdayCount ? `${schoolWideWeekdayCount} configured` : 'Not configured'} />
                  </div>

                  {canManageDefaults ? (
                    <div className="sap-section-card ssp-card ssp-default-times-card">
                      <div className="ssp-logo-heading">
                        <h3>Dismissal Times</h3>
                        <p>General and weekday fallback times for dismissal start.</p>
                      </div>
                      <SchoolDefaultTimesField
                        schoolId={school.id}
                        defaultDismissalTime={school.default_dismissal_time}
                        token={token}
                        onSaved={(patch) => setSchool((current) => (current ? { ...current, ...patch } : current))}
                      />
                      <SchoolWeeklyDismissalDefaultsField
                        schoolId={school.id}
                        defaults={defaults}
                        token={token}
                        disabled={!canManageDefaults}
                        onSaved={(savedRows) => {
                          setDefaults((current) => [
                            ...current.filter((row) => !isSchoolWideWeekdayDefault(row)),
                            ...savedRows,
                          ]);
                        }}
                      />
                    </div>
                  ) : null}

                  {canManageLogo ? (
                    <div className="sap-section-card ssp-card ssp-logo-card">
                      <div className="ssp-logo-heading">
                        <h3>School Logo</h3>
                        <p>Shown across the app, parent communications, and dismissal board.</p>
                      </div>
                      <SchoolLogoField
                        schoolId={school.id}
                        schoolName={school.school_nickname || school.legal_name}
                        value={school.logo_relative_path}
                        token={token}
                        onChange={(nextPath) => setSchool((current) => (current ? { ...current, logo_relative_path: nextPath } : current))}
                      />
                    </div>
                  ) : null}

                </section>
              </div>

              <div className="ssp-column">
                <section className="sap-parents-panel ssp-billing-panel">
                  <div className="sap-panel-heading">
                    <div>
                      <h2>Billing</h2>
                      <p>Manage your payments</p>
                    </div>
                  </div>

                  <div className="sap-section-card ssp-card">
                    <InfoRow label="Plan" value={`${formatLabel(school.type)} Program`} />
                    <InfoRow label="Status" value={formatLabel(school.billing_status)} />
                    <InfoRow label="Billing Cycle" value={formatLabel(school.payment_interval)} />
                    <InfoRow label="Next Billing Date" value={formatDate(school.trial_ends || school.trial_begins)} />
                    <InfoRow label="Payment Method" value={paymentSupportEmail ? 'Managed by billing team' : 'Not available'} />
                  </div>

                  <button
                    className="sa-btn sa-btn-primary ssp-support-btn"
                    onClick={handleBillingModalOpen}
                    aria-haspopup="dialog"
                    aria-expanded={billingModalOpen}
                  >
                    Contact Support / Billing
                  </button>
                </section>

                <section className="sap-parents-panel">
                  <div className="sap-panel-heading">
                    <div>
                      <h2>Legal</h2>
                      <p>Helpful policy and support links</p>
                    </div>
                  </div>

                  <div className="sap-section-card ssp-card">
                    <LegalRow
                      label="Privacy Policy"
                      value="View privacy policy"
                      href="https://car-pool.app/privacy-policy/"
                    />
                    <LegalRow
                      label="Terms of Service"
                      value="View terms of service"
                      href="https://car-pool.app/terms-of-service/"
                    />
                    <LegalRow label="Contact School" value={school.main_email || school.main_phone || 'School contact not listed'} />
                    <LegalRow label="App Version" value={APP_VERSION} />
                  </div>
                </section>
              </div>

              <div className="ssp-column">
                <section className="sap-parents-panel ssp-notification-panel">
                  <div className="sap-panel-heading">
                    <div>
                      <h2>Notifications</h2>
                      <p>Notifications are sent in real time during dismissal hours</p>
                    </div>
                  </div>

                  <div className="sap-section-card ssp-card">
                    <ToggleRow
                      label="Dismissal Started"
                      checked={notifications.dismissalStarted}
                      onToggle={() => toggle('dismissalStarted')}
                    />
                    <ToggleRow
                      label="System Alerts"
                      checked={notifications.systemAlerts}
                      onToggle={() => toggle('systemAlerts')}
                    />
                    <ToggleRow
                      label="School Calendar Updates"
                      checked={notifications.calendarUpdates}
                      onToggle={() => toggle('calendarUpdates')}
                    />
                    <div className="ssp-note">Client-side alerts may still show site-wide banners above.</div>
                  </div>
                </section>

                <section className="sap-parents-panel ssp-communication-panel">
                  <div className="sap-panel-heading">
                    <div>
                      <h2>Communication</h2>
                      <p>Control direct chat between staff and parents</p>
                    </div>
                  </div>

                  <div className="sap-section-card ssp-card">
                    <button
                      type="button"
                      className="ssp-toggle-row"
                      onClick={handleMessagingToggle}
                      disabled={!canManageMessaging || messagingSaving}
                      aria-pressed={messagingEnabled}
                    >
                      <span className="ssp-toggle-copy">
                        <span>Staff &amp; Parent Messaging</span>
                        <span className="ssp-toggle-help">
                          {messagingEnabled
                            ? 'Teachers and parents can start chats with each other.'
                            : 'Direct staff–parent chat is off. School administrators can still message anyone.'}
                        </span>
                      </span>
                      <span className={`ssp-toggle ${messagingEnabled ? 'on' : ''}`}>
                        <span className="ssp-toggle-knob"></span>
                      </span>
                    </button>
                    {messagingError ? <div className="ssp-billing-error">{messagingError}</div> : null}
                    <div className="ssp-note">
                      {canManageMessaging
                        ? 'Applies to the parent and staff apps. School administrators are never restricted.'
                        : 'Only administrators can change this setting.'}
                    </div>
                  </div>
                </section>

                <section className="sap-parents-panel ssp-grouping-panel">
                  <div className="sap-panel-heading">
                    <div>
                      <h2>Student Grouping</h2>
                      <p>Controls teacher dismissal board group options</p>
                    </div>
                  </div>

                  <div className="sap-section-card ssp-card">
                    <div className="ssp-option-list" role="radiogroup" aria-label="Student grouping mode">
                      {STUDENT_GROUPING_OPTIONS.map((option) => {
                        const active = studentGroupingMode === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            className={`ssp-option-row ${active ? 'active' : ''}`}
                            onClick={() => handleStudentGroupingChange(option.value)}
                            disabled={!canManageStudentGrouping || studentGroupingSaving}
                            role="radio"
                            aria-checked={active}
                          >
                            <span className="ssp-option-copy">
                              <span>{option.label}</span>
                              <span>{option.description}</span>
                            </span>
                            <span className={`ssp-radio ${active ? 'on' : ''}`}>
                              <span></span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {studentGroupingError ? <div className="ssp-billing-error">{studentGroupingError}</div> : null}
                    <div className="ssp-note">
                      {canManageStudentGrouping
                        ? 'Applies to staff webapp group selection before opening the live dismissal board.'
                        : 'Only administrators can change this setting.'}
                    </div>
                  </div>
                </section>
              </div>
            </div>

            {canManageGradeAliases ? (
              <section className="sap-parents-panel ssp-grade-alias-panel ssp-collapsible-panel">
                <button
                  type="button"
                  className="sap-panel-heading ssp-collapsible-heading"
                  onClick={() => setGradeAliasesOpen((current) => !current)}
                  aria-expanded={gradeAliasesOpen}
                  aria-controls="ssp-grade-alias-content"
                >
                  <div>
                    <h2>Grade Aliases</h2>
                    <p>Alternate grade names accepted for student and teacher imports.</p>
                  </div>
                  <span className={`ssp-collapse-icon ${gradeAliasesOpen ? 'open' : ''}`} aria-hidden="true"></span>
                </button>
                {gradeAliasesOpen ? (
                  <div id="ssp-grade-alias-content" className="ssp-collapsible-content">
                    <SchoolGradeAliasesField
                      schoolId={school.id}
                      school={school}
                      token={token}
                      disabled={!canManageGradeAliases}
                    />
                  </div>
                ) : null}
              </section>
            ) : null}

            <section className="sap-parents-panel ssp-onboarding-panel ssp-collapsible-panel">
              <button
                type="button"
                className="sap-panel-heading ssp-collapsible-heading"
                onClick={() => setOnboardingEmailOpen((current) => !current)}
                aria-expanded={onboardingEmailOpen}
                aria-controls="ssp-onboarding-email-content"
              >
                <div>
                  <h2>Onboarding Email</h2>
                  <p>Parent and staff account welcome messages</p>
                </div>
                <span className={`ssp-collapse-icon ${onboardingEmailOpen ? 'open' : ''}`} aria-hidden="true"></span>
              </button>

              {onboardingEmailOpen ? (
                <div id="ssp-onboarding-email-content" className="sap-section-card ssp-card ssp-collapsible-content">
                  <SchoolOnboardingEmailField
                    schoolId={school.id}
                    school={school}
                    token={token}
                    disabled={!canManageOnboardingEmail}
                    onSaved={(updatedSchool) => setSchool(updatedSchool)}
                  />
                  <SchoolOnboardingEmailField
                    audience="staff"
                    schoolId={school.id}
                    school={school}
                    token={token}
                    disabled={!canManageOnboardingEmail}
                  />
                </div>
              ) : null}
            </section>

            <section className="sap-parents-panel ssp-onboarding-panel ssp-collapsible-panel">
              <button
                type="button"
                className="sap-panel-heading ssp-collapsible-heading"
                onClick={() => setOnboardingWhatsAppOpen((current) => !current)}
                aria-expanded={onboardingWhatsAppOpen}
                aria-controls="ssp-onboarding-whatsapp-content"
              >
                <div>
                  <h2>Onboarding WhatsApp</h2>
                  <p>Parent account welcome message</p>
                </div>
                <span className={`ssp-collapse-icon ${onboardingWhatsAppOpen ? 'open' : ''}`} aria-hidden="true"></span>
              </button>

              {onboardingWhatsAppOpen ? (
                <div id="ssp-onboarding-whatsapp-content" className="sap-section-card ssp-card ssp-collapsible-content">
                  <SchoolOnboardingWhatsAppField
                    schoolId={school.id}
                    token={token}
                    disabled={!canManageOnboardingWhatsApp}
                  />
                  <SchoolOnboardingWhatsAppField
                    schoolId={school.id}
                    token={token}
                    disabled={!canManageOnboardingWhatsApp}
                    audience="staff"
                  />
                </div>
              ) : null}
            </section>
          </div>
        )}
      </div>
  );

  const billingModal = billingModalOpen ? (
    <BillingSupportModal
      billingMessage={billingMessage}
      billingSending={billingSending}
      billingError={billingError}
      billingSent={billingSent}
      onClose={handleBillingModalClose}
      onMessageChange={(event) => {
        setBillingMessage(event.target.value);
        if (billingError) setBillingError(null);
      }}
      onSend={handleBillingSend}
    />
  ) : null;

  if (bare) {
    return (
      <>
        {content}
        {billingModal}
      </>
    );
  }

  return (
    <SchoolLayout
      token={token}
      navigate={navigate}
      schoolId={schoolId}
      user={user}
      activePage="settings"
      breadcrumb="System Preferences"
    >
      {content}
      {billingModal}
    </SchoolLayout>
  );
}

function BillingSupportModal({
  billingMessage,
  billingSending,
  billingError,
  billingSent,
  onClose,
  onMessageChange,
  onSend,
}) {
  return (
    <div className="sd-support-overlay sd-lockdown-overlay" onClick={onClose}>
      <div
        className="sd-support-modal sd-support-modal-form ssp-billing-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ssp-billing-modal-title"
      >
        <button type="button" className="ssp-billing-close" onClick={onClose} aria-label="Close billing support modal">
          Close
        </button>

        {billingSent ? (
          <div className="ssp-billing-form ssp-billing-form-modal">
            <div className="ssp-billing-success">
              <strong>Message sent</strong>
              <p>Our billing team will follow up via email shortly.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="ssp-billing-modal-copy">
              <h2 id="ssp-billing-modal-title">How can the billing team help?</h2>
              <p>Send your billing question and our team will follow up by email.</p>
            </div>

            <div className="ssp-billing-form ssp-billing-form-modal">
              <textarea
                id="ssp-billing-textarea"
                className="ssp-billing-textarea"
                value={billingMessage}
                onChange={onMessageChange}
                placeholder="Describe your billing question..."
                disabled={billingSending}
              />
              {billingError ? <div className="ssp-billing-error">{billingError}</div> : null}
              <button
                type="button"
                className="sa-btn sa-btn-primary ssp-billing-send"
                onClick={onSend}
                disabled={!billingMessage.trim() || billingSending}
              >
                {billingSending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="ssp-info-row">
      <span className="ssp-info-label">{label}</span>
      <span className="ssp-info-value">{value || '—'}</span>
    </div>
  );
}

function LegalRow({ label, value, href }) {
  return (
    <div className="ssp-legal-row">
      <span>{label}</span>
      <span>
        {href ? (
          <a className="ssp-legal-link" href={href} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : (
          value
        )}
      </span>
    </div>
  );
}

function ToggleRow({ label, checked, onToggle }) {
  return (
    <button type="button" className="ssp-toggle-row" onClick={onToggle}>
      <span>{label}</span>
      <span className={`ssp-toggle ${checked ? 'on' : ''}`}>
        <span className="ssp-toggle-knob"></span>
      </span>
    </button>
  );
}
